import { MouseEvent } from "react";
import { Box, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import Warning from "../../../icons/Warning";

const StyledConditionLink = styled(RouterLink)({
  color: "#1976d2",
  font: "inherit",
  fontWeight: 500,
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
});

const DEFAULT_VIOLATION_TOOLTIP =
  "This condition has unresolved constraint violations";

type ConditionNameLinkProps = {
  name: string;
  to: { pathname: string; search: string };
  hasViolations: boolean;
  /**
   * Tooltip text shown on the warning icon. Defaults to a generic
   * "this condition has unresolved violations" message; callers (e.g. the
   * Not Submitted panel in StatusView) can override with action-oriented copy.
   */
  violationTooltip?: string;
  /**
   * Optional click handler on the link. Useful for callers that need to
   * stopPropagation so the click doesn't bubble into a surrounding row /
   * DataGrid cell handler.
   */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Optional pre-navigation guard. If supplied, called on plain left-clicks
   * (no Cmd/Ctrl/Shift/middle-click). If it returns false, navigation is
   * cancelled via preventDefault. Intended for unsaved-changes prompts.
   *
   * Modifier clicks (open in new tab/window) bypass the guard entirely so
   * the user can preserve their edits in the original tab.
   */
  onBeforeNavigate?: () => boolean;
};

const isModifierClick = (event: MouseEvent<HTMLAnchorElement>): boolean => {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  );
};

const ConditionNameLink = ({
  name,
  to,
  hasViolations,
  violationTooltip = DEFAULT_VIOLATION_TOOLTIP,
  onClick,
  onBeforeNavigate,
}: ConditionNameLinkProps) => {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (onBeforeNavigate && !isModifierClick(event)) {
      if (!onBeforeNavigate()) {
        event.preventDefault();
        return;
      }
    }
    onClick?.(event);
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      {hasViolations && (
        <Tooltip title={violationTooltip} placement="top" arrow>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <Warning style={{ fontSize: "16px", color: "#ff9800" }} />
          </span>
        </Tooltip>
      )}
      <StyledConditionLink to={to} onClick={handleClick}>
        {name}
      </StyledConditionLink>
    </Box>
  );
};

export default ConditionNameLink;
